import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

type UpdateRequest = {
  id: string;
  rowId: number;
  execute: () => Promise<void>;
  onComplete: () => void;
  onError: () => void;
};

type AllocationUpdateContextType = {
  isAnyRowUpdating: boolean;
  isRowUpdating: (rowId: number) => boolean;
  queueUpdate: (request: UpdateRequest) => void;
  lockRow: (rowId: number) => void;
  unlockRow: (rowId: number) => void;
};

/**
// manages multiple different updates for allocation rows with request queueing
// prevents Record Modified errors when rapidly editing fields by processing updates sequentially
// locks rows during updates to disable interaction and prevent double-submissions
*/
const AllocationUpdateContext = createContext<
  AllocationUpdateContextType | undefined
>(undefined);

export const AllocationUpdateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [updatingRows, setUpdatingRows] = useState<Set<number>>(new Set());
  const queueRef = useRef<UpdateRequest[]>([]);
  const isProcessingRef = useRef(false);

  // locks a row to prevent interaction during updates
  const lockRow = useCallback((rowId: number) => {
    setUpdatingRows((prev) => new Set(prev).add(rowId));
  }, []);

  // unlocks a row to allow interaction
  const unlockRow = useCallback((rowId: number) => {
    setUpdatingRows((prev) => {
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
  }, []);

  // references the queueRef, processes the updates sequentially
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || queueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    while (queueRef.current.length > 0) {
      const request = queueRef.current.shift();
      if (!request) continue;

      try {
        await request.execute();
        request.onComplete();
      } catch (error) {
        request.onError();
      }
    }

    isProcessingRef.current = false;
  }, []);

  // adds a request to the queue
  const queueUpdate = useCallback(
    (request: UpdateRequest) => {
      // check if there's already a queued update for this row
      const existingIndex = queueRef.current.findIndex(
        (r) => r.rowId === request.rowId && r.id === request.id,
      );

      // if there's already a queued update for this row, replace the existing request (debouncing effect)
      if (existingIndex !== -1) {
        // replace the existing request (debouncing effect)
        queueRef.current[existingIndex] = request;
      } else {
        queueRef.current.push(request);
      }

      processQueue();
    },
    [processQueue],
  );

  const isRowUpdating = useCallback(
    (rowId: number) => updatingRows.has(rowId),
    [updatingRows],
  );

  const isAnyRowUpdating = updatingRows.size > 0;

  return (
    <AllocationUpdateContext.Provider
      value={{
        isAnyRowUpdating,
        isRowUpdating,
        queueUpdate,
        lockRow,
        unlockRow,
      }}
    >
      {children}
    </AllocationUpdateContext.Provider>
  );
};

export const useAllocationUpdateContext = () => {
  const context = useContext(AllocationUpdateContext);
  if (!context) {
    throw new Error(
      "useAllocationUpdateContext must be used within AllocationUpdateProvider",
    );
  }
  return context;
};
